import { Box, Button, Stack } from "@mui/material"
import { useParams } from "react-router-dom"
import { Bound } from "lib/@components/binding/Bound"
import { useRefresh } from "lib/@hooks/useRefresh"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { NoResults } from "lib/@components/no-results/no-results"
import { TreeToolbar } from "slot-definitions"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { getProjectBridgeRootPath } from "./project-bridge-route-paths"
import { getProjectBridgeResolution } from "./controller/get-project-bridge-resolution"
import { saveProjectBridgeChangeCriticality } from "./controller/save-project-bridge-change-criticality"
import { saveProjectBridgeResolutionDecisions } from "./controller/save-project-bridge-resolution-decisions"
import { startProjectBridgeScheduleGeneration } from "./controller/start-project-bridge-schedule-generation"
import { isProjectBridgeRunInFlightForResolution } from "./project-bridge-detail-presenters"
import { ProjectBridgeResolutionContent } from "./project-bridge-resolution-content"

export function ProjectBridgeDetailContent() {
    const { id } = useParams()
    const refresh = useRefresh()
    const resolution = getProjectBridgeResolution.useResults({ id }, withInitialValue(IS_LOADING))
    const generationInFlight = isProjectBridgeRunInFlightForResolution(resolution)
    const canGenerate =
        Boolean(resolution?.importId) && Number(resolution?.readySectionCount || 0) > 0 && !generationInFlight

    if (resolution === IS_LOADING) return <LoadingScreen />
    if (!resolution) {
        return (
            <PageBreadcrumbContainer
                title="Project Bridge"
                parentCrumbs={[{ label: "Project Bridge", to: getProjectBridgeRootPath() }]}
            >
                <NoResults message="This Project Bridge schedule could not be found." />
            </PageBreadcrumbContainer>
        )
    }

    return (
        <PageBreadcrumbContainer
            title={`${resolution?.code} — Project Bridge`}
            parentCrumbs={[{ label: "Project Bridge", to: getProjectBridgeRootPath() }]}
        >
            <TreeToolbar.Plug
                deps={[canGenerate, generationInFlight, resolution?.importId, resolution?.facilitiesIqUrl]}
            >
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" size="small" disabled={!canGenerate} onClick={handleGenerate}>
                        Generate This Schedule
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!resolution?.facilitiesIqUrl}
                        href={resolution?.facilitiesIqUrl || undefined}
                        target="_blank"
                    >
                        Open Facilities IQ
                    </Button>
                </Stack>
            </TreeToolbar.Plug>

            <Box flex={1} sx={{ overflowY: "auto", py: 2, px: 1 }}>
                <Bound
                    target={resolution}
                    onDecisionChange={handleDecisionChange}
                    onTextChange={handleTextChange}
                    onCriticalityChange={handleCriticalityChange}
                    onCanonicalPropagationAction={handleCanonicalPropagationAction}
                    onDecideAll={handleDecideAll}
                >
                    <ProjectBridgeResolutionContent />
                </Bound>
            </Box>
        </PageBreadcrumbContainer>
    )

    async function handleDecisionChange(changeId, decision) {
        if (!resolution?.resolutionId) return
        await saveProjectBridgeResolutionDecisions({
            id: resolution.resolutionId,
            decisions: [{ changeId, decision }],
        })
        refresh()
    }

    async function handleTextChange(changeId, editedText, editScope = "shared") {
        if (!resolution?.resolutionId) return
        await saveProjectBridgeResolutionDecisions({
            id: resolution.resolutionId,
            decisions: [{ changeId, editedText, editScope }],
        })
        refresh()
    }

    async function handleCriticalityChange(changeId, criticality) {
        if (!resolution?.resolutionId) return
        await saveProjectBridgeChangeCriticality({ id: resolution.resolutionId, changeId, criticality })
        refresh()
    }

    async function handleCanonicalPropagationAction(changeId, propagationAction) {
        if (!resolution?.resolutionId) return
        await saveProjectBridgeResolutionDecisions({
            id: resolution.resolutionId,
            decisions: [{ changeId, propagationAction }],
        })
        refresh()
    }

    async function handleDecideAll(decision) {
        if (!resolution?.resolutionId) return
        const decisions = (resolution?.proposedChanges ?? []).map(function buildDecision(change) {
            return {
                changeId: change?.id,
                decision,
            }
        })
        await saveProjectBridgeResolutionDecisions({ id: resolution.resolutionId, decisions })
        refresh()
    }

    async function handleGenerate() {
        try {
            await startProjectBridgeScheduleGeneration({ overlapId: resolution?.id })
            refresh()
            successSnackbar("Project Bridge generation queued for this schedule.")
        } catch (error) {
            errorSnackbar(`Could not start Project Bridge generation: ${error?.message || error}`)
        }
    }
}
